import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DocumentArrowUpIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  TagIcon,
} from '@heroicons/react/24/solid';

interface DocumentUploaderProps {
  agentId: string;
  onUploadComplete: (results: any) => void;
  onClose: () => void;
}

interface UploadResult {
  fileName: string;
  fileSize: number;
  mimeType: string;
  chunksCreated: number;
  memoryEntryIds: string[];
}

interface UploadError {
  fileName: string;
  error: string;
}

export const DocumentUploader = ({
  agentId,
  onUploadComplete,
  onClose,
}: DocumentUploaderProps) => {
  const [t] = useTranslation('document_uploader');
  const [isUploading, setIsUploading] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [tags, setTags] = useState('');
  const [error, setError] = useState('');
  const [uploadResults, setUploadResults] = useState<{
    successful: UploadResult[];
    errors: UploadError[];
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const supportedTypes = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'text/csv',
    'text/plain',
    'application/rtf',
    'text/rtf',
  ];

  const supportedExtensions = ['pdf', 'doc', 'docx', 'csv', 'txt', 'rtf'];

  const isFileSupported = (file: File): boolean => {
    const extension = file.name.toLowerCase().split('.').pop() || '';
    return (
      supportedTypes.includes(file.type) ||
      supportedExtensions.includes(extension)
    );
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    const validFiles: File[] = [];
    const invalidFiles: string[] = [];

    selectedFiles.forEach((file) => {
      if (!isFileSupported(file)) {
        invalidFiles.push(file.name);
      } else if (file.size > 50 * 1024 * 1024) {
        // 50MB limit
        invalidFiles.push(`${file.name} (too large)`);
      } else {
        validFiles.push(file);
      }
    });

    if (invalidFiles.length > 0) {
      setError(`Unsupported files: ${invalidFiles.join(', ')}`);
    } else {
      setError('');
    }

    setFiles((prevFiles) => [...prevFiles, ...validFiles]);
  };

  const handleRemoveFile = (index: number) => {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const droppedFiles = Array.from(e.dataTransfer.files);
    const validFiles: File[] = [];
    const invalidFiles: string[] = [];

    droppedFiles.forEach((file) => {
      if (!isFileSupported(file)) {
        invalidFiles.push(file.name);
      } else if (file.size > 50 * 1024 * 1024) {
        invalidFiles.push(`${file.name} (too large)`);
      } else {
        validFiles.push(file);
      }
    });

    if (invalidFiles.length > 0) {
      setError(`Unsupported files: ${invalidFiles.join(', ')}`);
    } else {
      setError('');
    }

    setFiles((prevFiles) => [...prevFiles, ...validFiles]);
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      setError(t('select_files'));
      return;
    }

    try {
      setIsUploading(true);
      setError('');
      setUploadResults(null);

      // Create FormData
      const formData = new FormData();
      files.forEach((file) => {
        formData.append('files', file);
      });
      formData.append('agent_id', agentId);
      formData.append('tags', tags);

      // Use XMLHttpRequest to provide progress feedback (aggregate)
      const result = await new Promise<any>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open(
          'POST',
          `${import.meta.env.VITE_API_URL}/training/upload-documents`,
        );
        const token = localStorage.getItem('accessToken') || '';
        if (token) {
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        }

        xhr.upload.onprogress = (event) => {
          // Optional: Could map to a single progress bar if desired
        };

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              resolve(JSON.parse(xhr.responseText));
            } catch (e) {
              reject(e);
            }
          } else {
            reject(new Error(`Upload failed: ${xhr.statusText}`));
          }
        };

        xhr.onerror = () => reject(new Error('Network error during upload'));
        xhr.send(formData);
      });

      if (result.hasError) {
        throw new Error(result.message);
      }

      // Handle success
      setUploadResults(result.data);
      onUploadComplete(result.data);

      // Reset form after successful upload
      setFiles([]);
      setTags('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsUploading(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (file: File) => {
    const extension = file.name.toLowerCase().split('.').pop() || '';
    const iconClass = 'h-8 w-8 text-gray-500';

    switch (extension) {
      case 'pdf':
        return <DocumentArrowUpIcon className={`${iconClass} text-red-500`} />;
      case 'doc':
      case 'docx':
        return <DocumentArrowUpIcon className={`${iconClass} text-blue-500`} />;
      case 'csv':
        return (
          <DocumentArrowUpIcon className={`${iconClass} text-green-500`} />
        );
      case 'txt':
        return <DocumentArrowUpIcon className={`${iconClass} text-gray-500`} />;
      default:
        return <DocumentArrowUpIcon className={iconClass} />;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="max-h-[90vh] w-full max-w-4xl overflow-auto rounded-lg border bg-white p-6 shadow-lg">
        <div className="mb-6 flex items-center justify-between">
          <h3 className="text-xl font-semibold">{t('upload_documents')}</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {!uploadResults ? (
          <div className="space-y-6">
            {/* File Drop Zone */}
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                {t('documents')}
              </label>
              <div
                className="cursor-pointer rounded-lg border-2 border-dashed border-gray-300 p-8 text-center hover:bg-gray-50"
                onClick={() => fileInputRef.current?.click()}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
              >
                <DocumentArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                <p className="mt-2 text-sm text-gray-600">{t('drag_drop')}</p>
                <p className="mt-1 text-xs text-gray-400">
                  {t('supported_types')}
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  onChange={handleFileChange}
                  accept=".pdf,.doc,.docx,.csv,.txt,.rtf"
                  multiple
                />
              </div>
            </div>

            {/* Selected Files */}
            {files.length > 0 && (
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Selected Files ({files.length})
                </label>
                <div className="max-h-40 space-y-2 overflow-y-auto">
                  {files.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between rounded-md border bg-gray-50 p-3"
                    >
                      <div className="flex items-center">
                        <div className="flex-shrink-0">{getFileIcon(file)}</div>
                        <div className="ml-3">
                          <p className="text-sm font-medium text-gray-900">
                            {file.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {formatFileSize(file.size)}
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveFile(index)}
                        className="text-gray-400 hover:text-gray-500"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tags Input */}
            <div>
              <label
                htmlFor="tags"
                className="mb-2 block text-sm font-medium text-gray-700"
              >
                <TagIcon className="mr-1 inline h-4 w-4" />
                Tags (optional)
              </label>
              <input
                type="text"
                id="tags"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="Enter tags separated by commas (e.g., manual, documentation, faq)"
              />
              <p className="mt-1 text-xs text-gray-500">
                Tags help organize and filter your training data
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="flex items-center rounded-md bg-red-50 p-3">
                <ExclamationTriangleIcon className="mr-2 h-5 w-5 text-red-400" />
                <span className="text-sm text-red-600">{error}</span>
              </div>
            )}

            {/* Upload Button */}
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleUpload}
                disabled={files.length === 0 || isUploading}
                className={`rounded-md px-4 py-2 text-sm font-medium text-white ${
                  files.length === 0 || isUploading
                    ? 'cursor-not-allowed bg-gray-300'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {isUploading
                  ? 'Processing...'
                  : `Upload ${files.length} Document${
                      files.length !== 1 ? 's' : ''
                    }`}
              </button>
            </div>
          </div>
        ) : (
          /* Upload Results */
          <div className="space-y-6">
            <div className="text-center">
              <CheckCircleIcon className="mx-auto h-12 w-12 text-green-500" />
              <h4 className="mt-2 text-lg font-medium text-gray-900">
                Upload Complete
              </h4>
              <p className="text-sm text-gray-600">
                {uploadResults.successful.length} file(s) processed successfully
                {uploadResults.errors.length > 0 &&
                  `, ${uploadResults.errors.length} failed`}
              </p>
            </div>

            {/* Successful Uploads */}
            {uploadResults.successful.length > 0 && (
              <div>
                <h5 className="mb-2 text-sm font-medium text-gray-900">
                  Successfully Processed
                </h5>
                <div className="space-y-2">
                  {uploadResults.successful.map((result, index) => (
                    <div key={index} className="rounded-md bg-green-50 p-3">
                      <div className="flex items-center">
                        <CheckCircleIcon className="mr-2 h-5 w-5 text-green-400" />
                        <div>
                          <p className="text-sm font-medium text-green-800">
                            {result.fileName}
                          </p>
                          <p className="text-xs text-green-600">
                            Created {result.chunksCreated} memory entries from{' '}
                            {formatFileSize(result.fileSize)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Failed Uploads */}
            {uploadResults.errors.length > 0 && (
              <div>
                <h5 className="mb-2 text-sm font-medium text-gray-900">
                  Failed to Process
                </h5>
                <div className="space-y-2">
                  {uploadResults.errors.map((error, index) => (
                    <div key={index} className="rounded-md bg-red-50 p-3">
                      <div className="flex items-center">
                        <ExclamationTriangleIcon className="mr-2 h-5 w-5 text-red-400" />
                        <div>
                          <p className="text-sm font-medium text-red-800">
                            {error.fileName}
                          </p>
                          <p className="text-xs text-red-600">{error.error}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Close Button */}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentUploader;
